import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import spssQuestionsConfig from "./spssQuestionsConfig";

/* ── Persistence helpers ──────────────────────────────────────────────────── */

const STORAGE_KEY = "spss-registration-progress";
const SAVE_DEBOUNCE_MS = 500;

function readSavedProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      formData: parsed.formData || {},
      completedSections: new Set(parsed.completedSections || []),
      activeSection: parsed.activeSection || null,
      sectionStates: parsed.sectionStates || {},
    };
  } catch {
    return null;
  }
}

function persistProgress(state) {
  const {
    formData,
    completedSections,
    activeSection,
    sectionStates,
    baseRevealedIds,
    activeId,
  } = state;

  const serializableFormData = {};
  for (const [key, val] of Object.entries(formData)) {
    if (val instanceof File) continue;
    serializableFormData[key] = val;
  }

  const mergedSectionStates = { ...sectionStates };
  if (activeSection) {
    mergedSectionStates[activeSection] = {
      revealedIds: [...baseRevealedIds],
      activeId,
    };
  }

  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        formData: serializableFormData,
        completedSections: [...completedSections],
        activeSection,
        sectionStates: mergedSectionStates,
      })
    );
  } catch {
    // ignore
  }
}

function clearSavedProgress() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/* ── Section definitions ──────────────────────────────────────────────────── */

const SECTION_DEFS = {
  data: {
    key: "data",
    title: "Data Peserta",
    subtitle: "Informasi pribadi, kategori pendaftaran, dan data tim",
    icon: "fa-solid fa-user",
    includes: ["Kategori Peserta", "Data Diri", "Data Tim"],
  },
  documents: {
    key: "documents",
    title: "Dokumen Persyaratan",
    subtitle: "KTM, Twibbon, dan bukti Instagram Story",
    icon: "fa-solid fa-file-lines",
    includes: ["Upload Berkas"],
  },
  payment: {
    key: "payment",
    title: "Pembayaran",
    subtitle: "Informasi pembayaran dan bukti transfer",
    icon: "fa-solid fa-credit-card",
    includes: ["Pembayaran"],
  },
};

const SECTION_KEYS = ["data", "documents", "payment"];

// Set to true once section locking is desired for the backend/submission flow.
const LOCK_COMPLETED_SECTIONS = false;

/* ── Primary contact derivation ───────────────────────────────────────────── */
// Form SPSS memiliki banyak kumpulan field tergantung cabangnya (BINUSIAN/NON,
// GLANCE/SMILE, Individu/Kelompok). Data primer peserta diambil dari grup yang
// terisi sesuai cabang yang dipilih.

const CONTACT_GROUPS = [
  ["glance_binus_nama", "glance_binus_nim", "glance_binus_email", "glance_binus_whatsapp"],
  ["smile_individu_binus_nama", "smile_individu_binus_nim", "smile_individu_binus_email", "smile_individu_binus_whatsapp"],
  ["smile_kel_binus_ketua_nama", "smile_kel_binus_ketua_nim", "smile_kel_binus_ketua_email", "smile_kel_binus_ketua_whatsapp"],
  ["glance_nonbinus_nama", "glance_nonbinus_nim", "glance_nonbinus_email", "glance_nonbinus_whatsapp"],
  ["smile_individu_nonbinus_nama", "smile_individu_nonbinus_nim", "smile_individu_nonbinus_email", "smile_individu_nonbinus_whatsapp"],
  ["smile_kel_nonbinus_ketua_nama", "smile_kel_nonbinus_ketua_nim", "smile_kel_nonbinus_ketua_email", "smile_kel_nonbinus_ketua_whatsapp"],
];

function derivePrimaryContact(formData) {
  for (const [namaKey, nimKey, emailKey, waKey] of CONTACT_GROUPS) {
    if (formData[namaKey]) {
      return {
        nama_lengkap: formData[namaKey],
        nim: formData[nimKey] || "",
        email: formData[emailKey] || "",
        nomor_telepon: formData[waKey] || "",
      };
    }
  }
  return { nama_lengkap: "", nim: "", email: "", nomor_telepon: "" };
}

/* ── Shared field validation ──────────────────────────────────────────────── */
// Single source of truth for field-level validation. Returns an error message
// string or null. Used by per-step, per-section, and full-form validation so
// the same rules are enforced before proceeding and before submitting.

function evaluateQuestion(question, value) {
  if (question.type === "info") return null;

  if (question.required) {
    if (
      !value ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return "Bidang ini wajib diisi.";
    }
  }

  if (
    value &&
    question.validation?.pattern &&
    !question.validation.pattern.test(String(value))
  ) {
    return question.validation.patternMessage || "Format tidak valid.";
  }

  if (value && typeof value === "string") {
    if (
      question.validation?.minLength &&
      value.length < question.validation.minLength
    ) {
      return `Minimal ${question.validation.minLength} karakter.`;
    }
    if (
      question.validation?.maxLength &&
      value.length > question.validation.maxLength
    ) {
      return `Maksimal ${question.validation.maxLength} karakter.`;
    }
  }

  if (question.type === "file" && value && question.validation) {
    if (question.validation.maxSizeMB) {
      const maxBytes = question.validation.maxSizeMB * 1024 * 1024;
      if (value.size > maxBytes) {
        return `Ukuran file maksimal ${question.validation.maxSizeMB}MB.`;
      }
    }
    if (question.validation.accept) {
      const ext = "." + value.name.split(".").pop().toLowerCase();
      if (!question.validation.accept.includes(ext)) {
        return `Format yang diterima: ${question.validation.accept.join(", ")}.`;
      }
    }
  }

  return null;
}

/* ── Hook ─────────────────────────────────────────────────────────────────── */

/**
 * Custom hook that manages the section-based SPSS 2026 registration form.
 *
 * DESIGN:
 * - The form is divided into 3 sections: Data Peserta, Dokumen, Pembayaran.
 * - Each section uses the existing incremental question experience.
 * - Completed sections are locked and cannot be reopened.
 * - All state is persisted to localStorage with debounced autosave.
 */
function useSPSSRegistrationForm() {
  const saved = useMemo(() => readSavedProgress(), []);

  const [formData, setFormData] = useState(saved?.formData ?? {});
  const [errors, setErrors] = useState({});
  const [activeSection, setActiveSection] = useState(
    saved?.activeSection ?? null
  );
  const [completedSections, setCompletedSections] = useState(
    saved?.completedSections ?? new Set()
  );
  const [sectionStates, setSectionStates] = useState(
    saved?.sectionStates ?? {}
  );
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [baseRevealedIds, setBaseRevealedIds] = useState(() => {
    if (saved?.activeSection && saved?.sectionStates[saved.activeSection]) {
      return new Set(saved.sectionStates[saved.activeSection].revealedIds);
    }
    return new Set();
  });
  const [activeId, setActiveId] = useState(() => {
    if (saved?.activeSection && saved?.sectionStates[saved.activeSection]) {
      return saved.sectionStates[saved.activeSection].activeId;
    }
    return null;
  });

  const saveTimerRef = useRef(null);
  const skipNextSaveRef = useRef(true);
  const stateRef = useRef({});

  stateRef.current = {
    formData,
    completedSections,
    activeSection,
    sectionStates,
    baseRevealedIds,
    activeId,
  };

  // ── 1. Autosave (debounced) ────────────────────────────────────────────

  useEffect(() => {
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      persistProgress(stateRef.current);
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(saveTimerRef.current);
  });

  // ── 2. Visibility check ────────────────────────────────────────────────

  const isQuestionVisible = useCallback((q, fd) => {
    if (q.visible) return q.visible(fd);
    if (q.parentId) {
      if (Array.isArray(q.parentId)) {
        return q.parentId.every((pid, i) => {
          const expected = Array.isArray(q.parentValue)
            ? q.parentValue[i]
            : q.parentValue;
          return fd[pid] === expected;
        });
      }
      const parentAnswer = fd[q.parentId];
      if (parentAnswer === q.parentValue) return true;
      if (q.parentValueIn && q.parentValueIn.includes(parentAnswer)) return true;
      return false;
    }
    return true;
  }, []);

  // ── 3. Visible questions (filtered by active section) ──────────────────

  const visibleQuestions = useMemo(() => {
    if (!activeSection) return [];
    const includedSections = SECTION_DEFS[activeSection].includes;
    const result = [];
    const seenIds = new Set();
    for (const q of spssQuestionsConfig) {
      if (!includedSections.includes(q.section)) continue;
      if (!isQuestionVisible(q, formData)) continue;
      if (seenIds.has(q.id)) continue;
      result.push(q);
      seenIds.add(q.id);
    }
    return result;
  }, [activeSection, formData, isQuestionVisible]);

  // ── 4. Sub-sections (within active section) ────────────────────────────

  const sections = useMemo(() => {
    const map = new Map();
    for (const q of visibleQuestions) {
      if (q.type === "info") continue;
      if (!map.has(q.section)) map.set(q.section, []);
      map.get(q.section).push(q);
    }
    return Array.from(map.entries()).map(([name, qs]) => ({
      name,
      questions: qs,
    }));
  }, [visibleQuestions]);

  // ── 5. Revealed IDs ────────────────────────────────────────────────────

  const revealedIds = useMemo(() => {
    if (visibleQuestions.length === 0) return new Set();
    const result = new Set();
    const visibleIdSet = new Set(visibleQuestions.map((q) => q.id));
    for (const id of baseRevealedIds) {
      if (visibleIdSet.has(id)) result.add(id);
    }
    result.add(visibleQuestions[0].id);
    let changed = true;
    while (changed) {
      changed = false;
      for (let i = 0; i < visibleQuestions.length; i++) {
        const q = visibleQuestions[i];
        if (result.has(q.id)) continue;
        if (q.type === "info") {
          let lastInputBefore = null;
          for (let j = i - 1; j >= 0; j--) {
            if (visibleQuestions[j].type !== "info") {
              lastInputBefore = visibleQuestions[j];
              break;
            }
          }
          if (lastInputBefore && result.has(lastInputBefore.id)) {
            result.add(q.id);
            changed = true;
          }
        }
      }
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRevealedIds, visibleQuestions]);

  // ── 6. Initialise / repair activeId ────────────────────────────────────

  useEffect(() => {
    if (!activeSection || visibleQuestions.length === 0) return;

    if (activeId === null) {
      const firstInput = visibleQuestions.find((q) => q.type !== "info");
      if (firstInput) {
        setActiveId(firstInput.id);
        setBaseRevealedIds(new Set([firstInput.id]));
      }
      return;
    }

    if (!visibleQuestions.some((q) => q.id === activeId)) {
      for (let i = visibleQuestions.length - 1; i >= 0; i--) {
        if (
          visibleQuestions[i].type !== "info" &&
          revealedIds.has(visibleQuestions[i].id)
        ) {
          setActiveId(visibleQuestions[i].id);
          return;
        }
      }
    }
  }, [activeSection, activeId, revealedIds, visibleQuestions]);

  // ── 7. Derived helpers ─────────────────────────────────────────────────

  const activeIndex = visibleQuestions.findIndex((q) => q.id === activeId);
  const currentQuestion = visibleQuestions[activeIndex] || null;

  const inputQuestions = useMemo(
    () => visibleQuestions.filter((q) => q.type !== "info"),
    [visibleQuestions]
  );
  const totalSteps = inputQuestions.length;

  const isLastInSection = useMemo(() => {
    if (activeIndex < 0) return false;
    for (let i = activeIndex + 1; i < visibleQuestions.length; i++) {
      if (visibleQuestions[i].type !== "info") return false;
    }
    return true;
  }, [activeIndex, visibleQuestions]);

  const progress = useMemo(() => {
    if (totalSteps === 0) return 0;
    const answered = inputQuestions.filter((q) => {
      const val = formData[q.id];
      if (val === undefined || val === null || val === "") return false;
      if (Array.isArray(val) && val.length === 0) return false;
      return true;
    }).length;
    return Math.round((answered / totalSteps) * 100);
  }, [inputQuestions, formData, totalSteps]);

  // ── 8. Validation ──────────────────────────────────────────────────────

  const validateQuestion = useCallback(
    (questionId) => {
      const q = visibleQuestions.find((x) => x.id === questionId);
      if (!q || q.type === "info") return true;

      const message = evaluateQuestion(q, formData[q.id]);

      if (message) {
        setErrors((prev) => ({ ...prev, [q.id]: message }));
        return false;
      }

      setErrors((prev) => {
        const next = { ...prev };
        delete next[q.id];
        return next;
      });
      return true;
    },
    [visibleQuestions, formData]
  );

  const validateCurrentStep = useCallback(() => {
    if (!currentQuestion) return true;
    return validateQuestion(currentQuestion.id);
  }, [currentQuestion, validateQuestion]);

  // ── 9. Field updates ───────────────────────────────────────────────────

  const setFieldValue = useCallback((questionId, value) => {
    setFormData((prev) => ({ ...prev, [questionId]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  }, []);

  // ── 10. Navigation ─────────────────────────────────────────────────────

  const goNext = useCallback(() => {
    if (!validateCurrentStep()) return false;

    const idx = visibleQuestions.findIndex((q) => q.id === activeId);
    if (idx < 0) return false;

    let nextIdx = idx + 1;
    while (
      nextIdx < visibleQuestions.length &&
      visibleQuestions[nextIdx].type === "info"
    ) {
      nextIdx++;
    }

    if (nextIdx >= visibleQuestions.length) return false;

    const nextQ = visibleQuestions[nextIdx];

    const newIds = [];
    for (let i = idx + 1; i <= nextIdx; i++) {
      if (!revealedIds.has(visibleQuestions[i].id)) {
        newIds.push(visibleQuestions[i].id);
      }
    }

    if (newIds.length > 0) {
      setBaseRevealedIds((prev) => new Set([...prev, ...newIds]));
    }

    setActiveId(nextQ.id);
    setErrors({});
    return true;
  }, [activeId, visibleQuestions, revealedIds, validateCurrentStep]);

  const goBack = useCallback(() => {
    const idx = visibleQuestions.findIndex((q) => q.id === activeId);
    if (idx <= 0) return;

    let prevIdx = idx - 1;
    while (prevIdx >= 0 && visibleQuestions[prevIdx].type === "info") {
      prevIdx--;
    }

    if (prevIdx >= 0) {
      setActiveId(visibleQuestions[prevIdx].id);
      setErrors({});
    }
  }, [activeId, visibleQuestions]);

  const goToQuestion = useCallback(
    (questionId) => {
      if (revealedIds.has(questionId)) {
        setActiveId(questionId);
        setErrors({});
      }
    },
    [revealedIds]
  );

  const resetProgressFrom = useCallback(
    (questionId) => {
      const idx = visibleQuestions.findIndex((q) => q.id === questionId);
      if (idx < 0) return;

      const keepIds = new Set(
        visibleQuestions.slice(0, idx + 1).map((q) => q.id)
      );

      setFormData((prev) => {
        const next = {};
        for (const key of Object.keys(prev)) {
          if (keepIds.has(key)) {
            next[key] = prev[key];
          }
        }
        return next;
      });

      setBaseRevealedIds(
        new Set(visibleQuestions.slice(0, idx + 1).map((q) => q.id))
      );

      setActiveId(questionId);
      setErrors({});
    },
    [visibleQuestions]
  );

  // ── 11. Section management ─────────────────────────────────────────────

  const enterSection = useCallback(
    (sectionKey) => {
      if (LOCK_COMPLETED_SECTIONS && completedSections.has(sectionKey)) return;

      const includedSections = SECTION_DEFS[sectionKey].includes;
      const hasVisible = spssQuestionsConfig.some(
        (q) =>
          includedSections.includes(q.section) && isQuestionVisible(q, formData)
      );
      if (!hasVisible) return;

      setActiveSection(sectionKey);
      const saved = sectionStates[sectionKey];
      if (saved) {
        setBaseRevealedIds(new Set(saved.revealedIds));
        setActiveId(saved.activeId);
      } else {
        setBaseRevealedIds(new Set());
        setActiveId(null);
      }
      setErrors({});
    },
    [completedSections, sectionStates, formData, isQuestionVisible]
  );

  const leaveSection = useCallback(() => {
    if (!activeSection) return;
    setSectionStates((prev) => ({
      ...prev,
      [activeSection]: {
        revealedIds: [...baseRevealedIds],
        activeId,
      },
    }));
    setActiveSection(null);
    setBaseRevealedIds(new Set());
    setActiveId(null);
    setErrors({});
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [activeSection, baseRevealedIds, activeId]);

  const saveAndBack = useCallback(() => {
    if (!activeSection) return false;

    if (!validateCurrentStep()) return false;

    let valid = true;
    for (const q of inputQuestions) {
      const message = evaluateQuestion(q, formData[q.id]);
      if (message) {
        valid = false;
        setErrors((prev) => ({ ...prev, [q.id]: message }));
      }
    }
    if (!valid) return false;

    setSectionStates((prev) => ({
      ...prev,
      [activeSection]: {
        revealedIds: [...baseRevealedIds],
        activeId,
      },
    }));

    setCompletedSections((prev) => new Set([...prev, activeSection]));
    setActiveSection(null);
    setBaseRevealedIds(new Set());
    setActiveId(null);
    setErrors({});
    window.scrollTo({ top: 0, behavior: "instant" });
    return true;
  }, [
    activeSection,
    inputQuestions,
    formData,
    baseRevealedIds,
    activeId,
    validateCurrentStep,
  ]);

  const sectionStatus = useCallback(
    (sectionKey) => {
      if (completedSections.has(sectionKey)) return "completed";
      const state = sectionStates[sectionKey];
      if (!state || state.revealedIds.length === 0) return "not_started";
      return "in_progress";
    },
    [completedSections, sectionStates]
  );

  // ── 12. Submit ─────────────────────────────────────────────────────────

  const primaryContact = useMemo(
    () => derivePrimaryContact(formData),
    [formData]
  );

  // All visible (answered) questions across every section, grouped for the
  // pre-submission review step. Info cards are skipped — only entered data.
  const reviewSections = useMemo(() => {
    const result = [];
    for (const sectionKey of SECTION_KEYS) {
      const def = SECTION_DEFS[sectionKey];
      const questions = [];
      for (const q of spssQuestionsConfig) {
        if (!def.includes.includes(q.section)) continue;
        if (!isQuestionVisible(q, formData)) continue;
        if (q.type === "info") continue;
        questions.push(q);
      }
      if (questions.length > 0) {
        result.push({
          key: def.key,
          title: def.title,
          icon: def.icon,
          questions,
        });
      }
    }
    return result;
  }, [formData, isQuestionVisible]);

  /**
   * Validate the entire form (every visible question across all sections,
   * including the payment file) WITHOUT sending anything. Returns true when
   * the data is ready to be reviewed; otherwise sets errors and returns false.
   */
  const validateForSubmit = useCallback(() => {
    if (!validateCurrentStep()) return false;

    const validationErrors = {};
    let valid = true;
    for (const sectionKey of SECTION_KEYS) {
      const includedSections = SECTION_DEFS[sectionKey].includes;
      for (const q of spssQuestionsConfig) {
        if (!includedSections.includes(q.section)) continue;
        if (!isQuestionVisible(q, formData)) continue;
        if (q.type === "info") continue;
        const message = evaluateQuestion(q, formData[q.id]);
        if (message) {
          valid = false;
          validationErrors[q.id] = message;
        }
      }
    }

    if (!valid) {
      setErrors({
        ...validationErrors,
        __submit__:
          "Masih ada jawaban yang belum valid. Periksa kembali isian Anda sebelum mengirim.",
      });
      return false;
    }

    const paymentKey = formData.bukti_pembayaran_smile
      ? "bukti_pembayaran_smile"
      : formData.bukti_pembayaran_glance
        ? "bukti_pembayaran_glance"
        : null;

    if (!paymentKey || !(formData[paymentKey] instanceof File)) {
      setErrors({
        __submit__: "Mohon lengkapi bukti pembayaran terlebih dahulu.",
      });
      return false;
    }

    setErrors({});
    return true;
  }, [formData, isQuestionVisible, validateCurrentStep]);

  /**
   * Send the reviewed registration to the backend. Only reached after the
   * user confirms in the review step, so this performs the actual API request.
   * Guards against double submission while a request is in flight.
   */
  const confirmSubmit = useCallback(async () => {
    if (submitting) return false;
    setSubmitting(true);

    const paymentKey = formData.bukti_pembayaran_smile
      ? "bukti_pembayaran_smile"
      : formData.bukti_pembayaran_glance
        ? "bukti_pembayaran_glance"
        : null;

    if (!paymentKey || !(formData[paymentKey] instanceof File)) {
      setErrors({
        __submit__: "Mohon lengkapi bukti pembayaran terlebih dahulu.",
      });
      setSubmitting(false);
      return false;
    }

    try {
      const { nama_lengkap, nim, email, nomor_telepon } =
        derivePrimaryContact(formData);

      const fd = new FormData();
      fd.append("kategori", "SPSS");
      fd.append("nama_lengkap", nama_lengkap);
      fd.append("nim", nim);
      fd.append("email", email);
      fd.append("nomor_telepon", nomor_telepon);

      const serialized = {};
      for (const [key, value] of Object.entries(formData)) {
        if (value instanceof File) {
          fd.append(key, value);
          serialized[key] = value.name;
        } else if (value !== undefined && value !== null && value !== "") {
          serialized[key] = value;
        }
      }

      fd.append("bukti_pembayaran", formData[paymentKey]);
      fd.append("data", JSON.stringify(serialized));

      const baseUrl = process.env.REACT_APP_BASE_URL || "http://127.0.0.1:8000";
      await axios.post(`${baseUrl}/api/registrations`, fd);

      setSubmitted(true);
      clearSavedProgress();
      return true;
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.bukti_pembayaran?.[0] ||
        "Terjadi kesalahan saat mengirim pendaftaran. Silakan coba lagi.";
      setErrors({ __submit__: msg });
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [formData, submitting]);

  // ── 13. Reset entire form ──────────────────────────────────────────────

  const resetForm = useCallback(() => {
    setFormData({});
    setErrors({});
    setActiveSection(null);
    setCompletedSections(new Set());
    setSectionStates({});
    setBaseRevealedIds(new Set());
    setActiveId(null);
    setSubmitted(false);
    clearSavedProgress();
  }, []);

  return {
    formData,
    errors,
    activeId,
    activeIndex,
    activeSection,
    completedSections,
    currentQuestion,
    visibleQuestions,
    revealedIds,
    sections,
    totalSteps,
    progress,
    submitted,
    submitting,
    isLastInSection,
    sectionStatus,
    primaryContact,
    reviewSections,
    setFieldValue,
    goNext,
    goBack,
    goToQuestion,
    resetProgressFrom,
    enterSection,
    leaveSection,
    saveAndBack,
    validateForSubmit,
    confirmSubmit,
    resetForm,
  };
}

export { SECTION_DEFS, SECTION_KEYS };
export default useSPSSRegistrationForm;
